/**
 * ScribePixel
 *
 * This module is repsonible for loading a pixel with event data as query
 * parameters.
 *
 * @module    scribe_pixel
 * @author    Brian LaMattina <blamattina@wayfair.com>
 * @copyright 2016 Wayfair, LLC - All rights reserved
 */
/* eslint-env browser */
import $ from 'jquery';

const loggerName = 'ScribePixel';

/**
 * Construct a ScribePixel
 *
 * @class
 * @param {String} url url of the pixel
 * @param {ScribeEvent} event scribe event containing the data to set as query
 * paremeters
 */
class ScribePixel {
  constructor(config, url, event) {
    this.config = config;
    this.url = url;
    this.event = event;
    this.image = new window.Image(1, 1);
    this.sendBatchedBeaconPost = this.sendBatchedBeaconPost.bind(this);
    this.sendBatchedEventsForm = this.sendBatchedEventsForm.bind(this);
  }

  /**
   * Send a POST request to the Scribe pixel endpoint
   * @returns {*}
   */
  post() {
    if (typeof window !== 'undefined' && window.isTest) {
      // Rejecting here would actually break a test. Neat!
      return Promise.resolve();
    }
    const errorHandler = this.logPixelLoadError.bind(this, this.url);
    const event = this.event;

    const promise = $.ajax({
      url: this.url,
      method: 'POST',
      data: this.event,
    }).then(() => event);

    promise.fail(errorHandler);
    return promise;
  }

  /**
   * Load pixel
   *
   * @return {$.Deferred} promise that will resolve or reject when the pixel
   * loads or fails to load
   */
  load() {
    if (typeof window !== 'undefined' && window.isTest) {
      return Promise.resolve();
    }

    const {event} = this;
    const shouldUseAjax = event?.data?.eventType !== 'PageView';
    const url = `${this.url}?${$.param(event.toJSON())}`;
    const errorHandler = this.logPixelLoadError.bind(this, url);

    if (shouldUseAjax) {
      return $.ajax({
        url,
        method: 'GET',
        data: event.toJSON(),
        xhrFields: {
          withCredentials: true,
        },
      })
        .fail(errorHandler)
        .then(() => event);
    } else {
      const imagePromise = this.constructor.wrapImageInPromise(
        this.image,
        event
      );

      imagePromise.fail(errorHandler);

      this.image.src = url;

      return imagePromise;
    }
  }

  logPixelLoadError(url, error) {
    const message = 'Scribe pixel failed to load: ' + url;
    if (this.config.enablePixelLoadErrorLogging) {
      this.config.logger.error({message, name: loggerName});
    } else {
      this.config.logger.debug({message, name: loggerName});
    }

    return error;
  }

  static wrapImageInPromise(image, scribeEvent) {
    const deferred = new $.Deferred();
    image.onload = deferred.resolve.bind(deferred, scribeEvent);
    image.onerror = deferred.reject.bind(deferred, scribeEvent);
    return deferred.promise();
  }

  sendBatchedBeaconPost() {
    const dataToSend = new FormData();
    const DEFAULT_EVENT_INDEX = 0;
    const eventJSON = this.event.toJSON();
    Object.keys(eventJSON).forEach(key => {
      dataToSend.append(
        `events[${DEFAULT_EVENT_INDEX}][${key}]`,
        eventJSON[key]
      );
    });
    if (navigator.sendBeacon) {
      return Promise.resolve(navigator.sendBeacon(this.url, dataToSend));
    } else {
      return this.sendBatchedEventsForm(dataToSend);
    }
  }

  /**
   * Send batched events in form data
   *
   * This function is necessary when events have to be sent via ajax with
   * the content type as form data. Post() cannot be used with form data it already
   * specifies the data type as json.
   *
   * @param dataToSend Data to send to Scribe.
   */
  sendBatchedEventsForm(dataToSend) {
    const errorHandler = this.logPixelLoadError.bind(this, this.url);

    const promise = $.ajax({
      url: this.url,
      method: 'POST',
      data: dataToSend,
      contentType: false,
      processData: false,
    });

    promise.fail(errorHandler);

    return promise;
  }
}

export default ScribePixel;
